import { ModuleInputType } from '../enums/module-input-type.enum';
import { StringResource } from './module-control-specification.model';
import { ModuleSpecification } from './module-specification.model';

export type DefaultValueFromSpecs = Range | string | string[] | number | boolean | undefined;

export abstract class StepParameter {
    parameterType: 'static' | 'stepOutput';
    constructor(public key: string) {}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
    getValue(): any | undefined {
        if (this instanceof StaticParameter) {
            return this.value;
        } else {
            return undefined;
        }
    }
}

export class StaticParameter<TModuleParameter extends ModuleParameter, TValue> extends StepParameter {
    protected _value?: TValue;

    get value(): TValue | undefined {
        return this._value;
    }

    set value(value: TValue | undefined) {
        this._value = value;
    }

    constructor(
        public moduleParameter: TModuleParameter,
        value?: TValue,
    ) {
        super(moduleParameter.key);
        this._value = value;
        this.parameterType = 'static';
    }
}

export class StepOutputParameter extends StepParameter {
    constructor(
        public sourceFlowId: string,
        public type: ModuleInputType,
        public sourceOutputKey: string,
        targetKey: string,
    ) {
        super(targetKey);
        this.parameterType = 'stepOutput';
    }
}

export class ModuleParameter {
    label: string;
    key: string;
    type: ModuleInputType;
    description?: string;

    /**
     * Items, the user can select of (defined in dropdowns and radiobutton groups).
     * Undefined for all other types.
     */
    items?: StringResource[];

    constructor(key: string, label: string, type: ModuleInputType, description: string, items?: StringResource[]) {
        this.key = key;
        this.label = label;
        this.type = type;
        this.description = description;
        this.items = items;
    }
}

export interface IVersionReadModel {
    id: string;
    versionNumber: number;
    specification: ModuleSpecification;
    moduleId: string;
    isOpenSource: boolean;
}

export class Range {
    from: number;
    to: number;
}

class IntegerModuleParameter extends ModuleParameter {
    defaultValue?: number;
}
class NumberModuleParameter extends ModuleParameter {
    defaultValue?: number;
}

export class StringModuleParameter extends ModuleParameter {
    defaultValue?: string;
}
export class FileModuleParameter extends ModuleParameter {
    defaultValue?: string;
}

class RangeModuleParameter extends ModuleParameter {
    defaultValue?: Range;
}
class ChoiceBinaryModuleParameter extends ModuleParameter {
    defaultValue?: boolean;
}
class ChoiceSingleModuleParameter extends ModuleParameter {
    defaultValue?: string;
}
export class ListFileModuleParameter extends ModuleParameter {
    defaultValue: string[] = [];
}
export class FileInputModuleParameter extends ModuleParameter {
    defaultValue: string[] = [];
}

export class IntegerStepParameter extends StaticParameter<IntegerModuleParameter, number> {
    constructor(moduleParameter: IntegerModuleParameter, defaultValue?: number) {
        super(moduleParameter, defaultValue);
    }
}
export class NumberStepParameter extends StaticParameter<NumberModuleParameter, number> {
    constructor(moduleParameter: IntegerModuleParameter, defaultValue?: number) {
        super(moduleParameter, defaultValue);
    }
}
export class StringStepParameter extends StaticParameter<StringModuleParameter, string> {
    constructor(moduleParameter: StringModuleParameter, defaultValue?: string) {
        super(moduleParameter, defaultValue);
    }
}

export class RangeStepParameter extends StaticParameter<RangeModuleParameter, Range> {
    constructor(moduleParameter: RangeModuleParameter, defaultValue?: Range) {
        super(moduleParameter, defaultValue);
    }
}
export class FileStepParameter extends StaticParameter<FileModuleParameter, string> {
    constructor(moduleParameter: FileModuleParameter, defaultValue?: string) {
        super(moduleParameter, defaultValue);
    }
}
export class ChoiceBinaryStepParameter extends StaticParameter<ChoiceBinaryModuleParameter, boolean> {
    constructor(moduleParameter: ChoiceBinaryModuleParameter, defaultValue?: boolean) {
        super(moduleParameter, defaultValue);
    }
}
export class ChoiceSingleStepParameter extends StaticParameter<ChoiceSingleModuleParameter, string> {
    constructor(moduleParameter: ChoiceSingleModuleParameter, defaultValue?: string) {
        super(moduleParameter, defaultValue);
    }
}
export class ListFileStepParameter extends StaticParameter<ListFileModuleParameter, string[]> {
    constructor(moduleParameter: ListFileModuleParameter, defaultValue?: string[]) {
        const defaultValueParam: string[] = defaultValue != null ? defaultValue : [];
        super(moduleParameter, defaultValueParam);
    }
}

export class FileInputStepParameter extends StaticParameter<FileInputModuleParameter, string[]> {
    constructor(moduleParameter: FileInputModuleParameter, defaultValue?: string[]) {
        const defaultValueParam: string[] = defaultValue != null ? defaultValue : [];
        super(moduleParameter, defaultValueParam);
    }
}

export function isInputTypeStatic(inputType: ModuleInputType): boolean {
    return inputType !== ModuleInputType.File && inputType !== ModuleInputType.ListFile && inputType !== ModuleInputType.FileInput;
}

export function isAssignable(outputParameterType: ModuleInputType, inputParameterType: ModuleInputType): boolean {
    return outputParameterType === inputParameterType;
}

export function getStepParameterAccordingToType(param: ModuleParameter, defaultValueFromSpec?: DefaultValueFromSpecs): StepParameter {
    let parameter: StepParameter;
    switch (param.type) {
        case ModuleInputType.Integer:
        case ModuleInputType.Number:
            parameter = new IntegerStepParameter(param, defaultValueFromSpec as number | undefined);
            break;

        case ModuleInputType.String:
            parameter = new StringStepParameter(param, defaultValueFromSpec as string | undefined);
            break;

        case ModuleInputType.Range:
            parameter = new RangeStepParameter(param, defaultValueFromSpec as Range | undefined);
            break;

        case ModuleInputType.ChoiceBinary:
            parameter = new ChoiceBinaryStepParameter(param, defaultValueFromSpec as boolean | undefined);
            break;

        case ModuleInputType.ChoiceSingle:
            parameter = new ChoiceSingleStepParameter(param, defaultValueFromSpec as string | undefined);
            break;
        case ModuleInputType.File:
            parameter = new FileStepParameter(param);
            break;

        case ModuleInputType.ListFile:
            parameter = new ListFileStepParameter(param as ListFileModuleParameter);
            break;
        case ModuleInputType.FileInput:
            parameter = new FileInputStepParameter(param as FileInputModuleParameter, defaultValueFromSpec as string[] | undefined);
            break;

        default:
            throw new Error('Parameter class is not found for type: ' + param.type);
    }

    return parameter;
}
